import { getSessionUUID } from '../components/lib/AuthToken';
import logger from "../utils/logger";

/**
 * Fetch job requests for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 */
export const fetch_routing_requests = async (date) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const response = await fetch(`/routing/requests?date=${encodeURIComponent(date)}`, requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            data: response.ok ? data : null,
            error: response.ok ? null : (data.error || 'Failed to fetch job requests')
        };
    } catch (err) {
        logger.error("Error fetching routing requests:", err);
        return {
            status: 500,
            data: null,
            error: err.message || 'An error occurred while fetching job requests'
        };
    }
};

/**
 * Fetch monthly pending routing requests for the calendar overview
 * @param {string} startDate - Start Date in YYYY-MM-DD format
 * @param {string} endDate - End Date in YYYY-MM-DD format
 */
export const fetch_monthly_pending_requests = async (startDate, endDate) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
        };

        const cleanStart = startDate.replace(/\//g, '-');
        const cleanEnd = endDate.replace(/\//g, '-');

        const response = await fetch(`/routing/requests?start_date=${encodeURIComponent(cleanStart)}&end_date=${encodeURIComponent(cleanEnd)}`, requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            data: response.ok ? data : null,
            error: response.ok ? null : (data.error || 'Failed to fetch monthly requests')
        };
    } catch (err) {
        logger.error("Error fetching monthly routing requests:", err);
        return {
            status: 500,
            data: null,
            error: err.message || 'An error occurred while fetching monthly requests'
        };
    }
};

/**
 * Submit a new job request
 * @param {object} requestData - The request details (date, company, address, postcode, route, pallets, weight, booked_time, comments)
 */
export const create_routing_request = async (requestData) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(requestData)
        };

        const response = await fetch('/routing/requests/create', requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            success: response.ok,
            data: response.ok ? data.data : null,
            error: response.ok ? null : (data.error || 'Failed to submit job request')
        };
    } catch (err) {
        logger.error("Error creating routing request:", err);
        return {
            status: 500,
            success: false,
            data: null,
            error: err.message || 'An error occurred while submitting job request'
        };
    }
};

/**
 * Cancel a pending request
 * @param {number} requestId - The request database ID
 */
export const cancel_routing_request = async (requestId) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/routing/requests/${requestId}/cancel`, requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            success: response.ok,
            data: response.ok ? data.data : null,
            error: response.ok ? null : (data.error || 'Failed to cancel job request')
        };
    } catch (err) {
        logger.error("Error cancelling routing request:", err);
        return {
            status: 500,
            success: false,
            data: null,
            error: err.message || 'An error occurred while cancelling job request'
        };
    }
};

/**
 * Accept a pending request (gated to perm_routing_edit)
 * @param {number} requestId - The request database ID
 */
export const accept_routing_request = async (requestId) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/routing/requests/${requestId}/accept`, requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            success: response.ok,
            data: response.ok ? data.route : null,
            error: response.ok ? null : (data.error || 'Failed to accept job request')
        };
    } catch (err) {
        logger.error("Error accepting routing request:", err);
        return {
            status: 500,
            success: false,
            data: null,
            error: err.message || 'An error occurred while accepting job request'
        };
    }
};

/**
 * Decline a pending request (gated to perm_routing_edit)
 * @param {number} requestId - The request database ID
 * @param {string} declineReason - Reason for declining the request
 */
export const decline_routing_request = async (requestId, declineReason) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ decline_reason: declineReason })
        };

        const response = await fetch(`/routing/requests/${requestId}/decline`, requestOptions);
        const data = await response.json();

        return {
            status: response.status,
            success: response.ok,
            data: response.ok ? data.data : null,
            deleted_route: response.ok ? data.deleted_route : null,
            error: response.ok ? null : (data.error || 'Failed to decline job request')
        };
    } catch (err) {
        logger.error("Error declining routing request:", err);
        return {
            status: 500,
            success: false,
            data: null,
            error: err.message || 'An error occurred while declining job request'
        };
    }
};
